import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAuthHeaders } from '../lib/auth';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  BookOpen, 
  Clock, 
  Quote, 
  Lightbulb, 
  Sparkles,
  ExternalLink,
  Share2,
  Bookmark,
  ChevronRight,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { ScrollArea } from '../components/ui/scroll-area';

const PAID_TIERS = [
  'founder_monthly', 'pastor_pro', 'founder_lifetime', 'seminary_annual',
  'ministry_plus', 'church_team', 'pro',
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResultsPage() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [sermon, setSermon] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('summary');
  const [user, setUser] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadSermonAndUser();
  }, [analysisId]);

  const loadSermonAndUser = async () => {
    try {
      // Load user data
      const userResponse = await fetch(`${API}/auth/me`, { headers: { ...getAuthHeaders() } });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
      
      // Load sermon
      const response = await fetch(`${API}/sermons/${analysisId}`, {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSermon(data);
      } else {
        toast.error('Sermon not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to load sermon:', error);
      toast.error('Failed to load sermon');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getYouTubeVideoId = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const openYouTubeAtTimestamp = (seconds) => {
    const videoId = getYouTubeVideoId(sermon?.youtube_url);
    if (videoId) {
      window.open(`https://www.youtube.com/watch?v=${videoId}&t=${Math.floor(seconds)}s`, '_blank');
    }
  };

  const handleExportPDF = async () => {
    // Check if user has export access
    const canExport = PAID_TIERS.includes(user?.subscription_tier) || user?.is_early_adopter;
    
    if (!canExport) {
      toast.error('PDF export is available for Pro subscribers and early adopters. Please upgrade to Pro.');
      return;
    }
    
    setIsExporting(true);
    
    try {
      const response = await fetch(`${API}/sermons/${analysisId}/export-pdf`, {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sermon.video_title || 'sermon_analysis'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF exported successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to export PDF');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Check if user can export PDFs
  const canExportPDF = PAID_TIERS.includes(user?.subscription_tier) || user?.is_early_adopter;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!sermon) {
    return null;
  }

  const sections = [
    { id: 'summary', label: 'Summary', icon: Sparkles },
    { id: 'scriptures', label: 'Scriptures', icon: BookOpen },
    { id: 'timestamps', label: 'Timestamps', icon: Clock },
    { id: 'quotes', label: 'Quotes', icon: Quote },
    { id: 'applications', label: 'Applications', icon: Lightbulb },
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]" data-testid="results-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-stone-600 hover:text-[#1e3a8a]"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Back to Library</span>
            </Link>
            
            <span className="font-serif font-bold text-[#1e3a8a]">SermonSift</span>
            
            <div className="flex items-center gap-2">
              {/* Export PDF Button */}
              <Button
                onClick={handleExportPDF}
                disabled={isExporting}
                variant={canExportPDF ? "outline" : "ghost"}
                size="sm"
                className={`flex items-center gap-2 ${
                  canExportPDF 
                    ? 'text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white border-[#1e3a8a]/30' 
                    : 'text-stone-400 cursor-not-allowed'
                }`}
                title={canExportPDF ? 'Export as PDF' : 'PDF export is available for Pro subscribers'}
                data-testid="export-pdf-btn"
              >
                {isExporting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                    <span className="hidden sm:inline">Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Export PDF</span>
                  </>
                )}
              </Button>
              
              {sermon.youtube_url && (
                <a 
                  href={sermon.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-stone-600 hover:text-[#1e3a8a] hover:bg-stone-100 rounded-lg"
                  data-testid="watch-on-youtube-btn"
                  title="Watch on YouTube"
                >
                  <ExternalLink className="w-5 h-5" strokeWidth={1.5} />
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-8">
          {/* Main Content */}
          <main>
            {/* Title Section */}
            <div className="mb-8 animate-fade-in">
              <h1 className="font-serif text-2xl lg:text-3xl font-bold text-[#1e3a8a] mb-2">
                {sermon.video_title || 'Sermon Analysis'}
              </h1>
              {sermon.preacher && sermon.preacher !== 'Unknown' && (
                <p className="text-lg text-stone-600">{sermon.preacher}</p>
              )}
              
              {/* Key Themes Tags */}
              {sermon.key_themes?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {sermon.key_themes.map((theme, i) => (
                    <span 
                      key={i} 
                      className="text-sm bg-[#1e3a8a]/10 text-[#1e3a8a] px-3 py-1 rounded-full"
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Section Navigation */}
            <div className="lg:hidden mb-6">
              <ScrollArea className="w-full">
                <div className="flex gap-2 pb-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                        activeSection === section.id 
                          ? 'bg-[#1e3a8a] text-white' 
                          : 'bg-white text-stone-600 border border-stone-200'
                      }`}
                    >
                      <section.icon className="w-4 h-4" strokeWidth={1.5} />
                      {section.label}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Summary Section */}
            <section
              id="summary"
              className={`mb-10 ${activeSection !== 'summary' ? 'hidden lg:block' : ''}`}
              data-testid="summary-section"
            >
              <div className="results-section-header">
                <Sparkles className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Summary</h3>
              </div>
              <div className="card p-6 space-y-5">
                {sermon.outline?.length > 0 ? (
                  sermon.outline.map((item, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-semibold text-stone-800 text-base">{item.heading}</h4>
                        {item.time != null && sermon.youtube_url && (
                          <button
                            onClick={() => openYouTubeAtTimestamp(item.time)}
                            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-[#1e3a8a]/10 text-[#1e3a8a] hover:bg-[#0ea5e9] hover:text-white transition-colors"
                            title={`Jump to ${formatTimestamp(item.time)} on YouTube`}
                          >
                            <Clock className="w-3 h-3" />
                            {formatTimestamp(item.time)}
                          </button>
                        )}
                      </div>
                      <p className="text-stone-600 leading-relaxed">{item.content}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-700 leading-relaxed whitespace-pre-wrap text-lg" style={{ lineHeight: '1.8' }}>
                    {sermon.summary}
                  </p>
                )}
              </div>
            </section>

            {/* Scripture References */}
            <section 
              id="scriptures" 
              className={`mb-10 ${activeSection !== 'scriptures' ? 'hidden lg:block' : ''}`}
              data-testid="scriptures-section"
            >
              <div className="results-section-header">
                <BookOpen className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Scripture References</h3>
                <span className="text-sm text-stone-500">({sermon.scripture_references?.length || 0})</span>
              </div>
              
              {sermon.scripture_references?.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {sermon.scripture_references.map((ref, i) => (
                    <div key={i} className="card p-4">
                      <div className="scripture-badge mb-2">
                        {ref.book} {ref.chapter}:{ref.verse}
                      </div>
                      {ref.context && (
                        <p className="text-sm text-stone-600">{ref.context}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500">No scripture references detected.</p>
              )}
            </section>

            {/* Timestamps */}
            <section 
              id="timestamps" 
              className={`mb-10 ${activeSection !== 'timestamps' ? 'hidden lg:block' : ''}`}
              data-testid="timestamps-section"
            >
              <div className="results-section-header">
                <Clock className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Key Timestamps</h3>
              </div>
              
              {sermon.timestamps?.length > 0 ? (
                <div className="space-y-3">
                  {sermon.timestamps.map((ts, i) => (
                    <button
                      key={i}
                      onClick={() => openYouTubeAtTimestamp(ts.time)}
                      className="card p-4 w-full text-left group hover:border-[#0ea5e9] transition-colors"
                      data-testid={`timestamp-${i}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="timestamp-pill group-hover:bg-[#0ea5e9] group-hover:text-white">
                          {formatTimestamp(ts.time)}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-stone-800">{ts.label}</p>
                          {ts.description && (
                            <p className="text-sm text-stone-500 mt-1">{ts.description}</p>
                          )}
                        </div>
                        <ExternalLink className="w-4 h-4 text-stone-400 group-hover:text-[#0ea5e9] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500">No timestamps available.</p>
              )}
            </section>

            {/* Key Quotes */}
            <section 
              id="quotes" 
              className={`mb-10 ${activeSection !== 'quotes' ? 'hidden lg:block' : ''}`}
              data-testid="quotes-section"
            >
              <div className="results-section-header">
                <Quote className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Key Quotes</h3>
              </div>
              
              {sermon.key_quotes?.length > 0 ? (
                <div className="space-y-4">
                  {sermon.key_quotes.map((quote, i) => (
                    <blockquote key={i} className="quote-block px-6 py-4">
                      "{quote}"
                    </blockquote>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500">No key quotes extracted.</p>
              )}
            </section>

            {/* Application Points */}
            <section 
              id="applications" 
              className={`mb-10 ${activeSection !== 'applications' ? 'hidden lg:block' : ''}`}
              data-testid="applications-section"
            >
              <div className="results-section-header">
                <Lightbulb className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                <h3>Practical Applications</h3>
              </div>
              
              {sermon.application_points?.length > 0 ? (
                <div className="card p-6">
                  <ul className="space-y-4">
                    {sermon.application_points.map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#1e3a8a] text-white text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <p className="text-stone-700">{point}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-stone-500">No application points extracted.</p>
              )}
            </section>
          </main>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              {/* Section Navigation */}
              <div className="card p-4 mb-6">
                <h4 className="font-medium text-stone-800 mb-3">Jump to Section</h4>
                <nav className="space-y-1">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-stone-600 hover:bg-stone-100 hover:text-[#1e3a8a] transition-colors"
                    >
                      <section.icon className="w-4 h-4" strokeWidth={1.5} />
                      {section.label}
                    </a>
                  ))}
                </nav>
              </div>

              {/* Quick Stats */}
              <div className="card p-4">
                <h4 className="font-medium text-stone-800 mb-3">Quick Stats</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Scriptures</span>
                    <span className="font-medium">{sermon.scripture_references?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Timestamps</span>
                    <span className="font-medium">{sermon.timestamps?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Key Quotes</span>
                    <span className="font-medium">{sermon.key_quotes?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500">Applications</span>
                    <span className="font-medium">{sermon.application_points?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-stone-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-sm text-stone-400">
          <p>SermonSift • A Bethany Labs product</p>
        </div>
      </footer>
    </div>
  );
}
